import { Link } from "react-router-dom";
import { ArrowRight, Clock, CalendarDays, MapPin, Users, Award, Quote } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../ui/accordion";
import { StatusBadge } from "../common";
import { money, fmtDate, fmtTime, track } from "../../lib/api";

export function ProgramCard({ p, i = 0 }) {
  return (
    <article className="card group flex flex-col overflow-hidden transition-[box-shadow,transform] duration-300 hover:-translate-y-1 hover:shadow-xl fade-up" style={{ animationDelay: `${i * 60}ms` }} data-testid={`program-card-${p.slug}`}>
      <div className="relative aspect-[16/9] overflow-hidden bg-slate-100">
        <img src={p.image} alt={`${p.name} training at Healthcare Resources`} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
        <div className="absolute left-3 top-3"><StatusBadge status={p.status} /></div>
      </div>
      <div className="flex flex-1 flex-col p-6">
        <h3 className="font-heading text-xl font-bold text-slate-900">{p.name}</h3>
        <p className="mt-2 line-clamp-3 text-sm text-slate-600">{p.short_description}</p>
        <div className="mt-4 flex flex-wrap gap-2 text-xs font-semibold text-slate-600">
          {p.duration && <span className="flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1"><Clock className="h-3.5 w-3.5" />{p.duration.split("•")[0]}</span>}
          {p.format && <span className="rounded-full bg-slate-100 px-2.5 py-1">{p.format}</span>}
          {(p.funding || []).filter((f) => f !== "Self Pay").map((f) => <span key={f} className="rounded-full bg-red-50 px-2.5 py-1 text-red-700">{f}</span>)}
        </div>
        <div className="mt-auto flex items-end justify-between pt-5">
          <div>
            {p.pricing?.show_public !== false && p.pricing?.base_tuition > 0 && <p className="text-xs text-slate-500">Tuition from <span className="font-heading text-lg font-extrabold text-slate-900">{money(p.pricing.base_tuition)}</span></p>}
            {p.next_class && <p className="text-xs font-semibold text-emerald-700">Next start: {fmtDate(p.next_class.start_date)}</p>}
          </div>
          <Link to={`/programs/${p.slug}`} className="flex items-center gap-1 text-sm font-bold text-red-600 hover:gap-2 transition-[gap]" data-testid={`program-card-link-${p.slug}`}>Details <ArrowRight className="h-4 w-4" /></Link>
        </div>
      </div>
    </article>
  );
}

export function PayLinks({ links = [], title = "Self Pay — secure Square checkout", note = true }) {
  if (!links?.length) return null;
  return (
    <div className="mt-5 rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid="square-pay-links">
      <h3 className="text-sm font-bold">{title}</h3>
      <div className="mt-3 space-y-2">
        {links.map((l) => (
          <a key={l.url} href={l.url} target="_blank" rel="noopener noreferrer" onClick={() => track("cta_click", { label: l.label })}
            className="btn-dark btn-sm w-full" data-testid={`pay-link-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{l.label}<ArrowRight className="h-4 w-4" aria-hidden /></a>
        ))}
      </div>
      {note && <p className="mt-3 text-xs text-slate-500">Opens Square in a new tab. After paying, return to your <Link to="/student/payments" className="font-semibold text-red-600">student portal</Link> and submit your payment proof. Payments are confirmed only after our team verifies them.</p>}
    </div>
  );
}

export function SeatBadge({ c }) {
  if (["Registration Closed", "Full", "Waitlist"].includes(c.status)) return <StatusBadge status={c.status} />;
  if (c.seats_left <= 0) return <StatusBadge status="Waitlist" />;
  const tone = c.seats_left <= 3 ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-emerald-50 text-emerald-700 border-emerald-200";
  return <span className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-bold ${tone}`} data-testid="seats-left"><Users className="h-3.5 w-3.5" />{c.seats_left} seats left</span>;
}

export function ClassCard({ c, showProgram = true }) {
  const open = c.seats_left > 0 && !["Registration Closed", "Full"].includes(c.status);
  return (
    <div className="card flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between" data-testid={`class-card-${c.id}`}>
      <div className="flex gap-4">
        <div className="grid h-16 w-16 shrink-0 place-items-center rounded-xl bg-slate-900 text-center text-white">
          <div><p className="text-[10px] font-bold uppercase text-red-400">{fmtDate(c.start_date).split(" ")[0]}</p><p className="font-heading text-2xl font-extrabold leading-none">{fmtDate(c.start_date).split(" ")[1]?.replace(",", "")}</p></div>
        </div>
        <div>
          {showProgram && <p className="eyebrow">{c.program_name}</p>}
          <h3 className="font-heading font-bold text-slate-900">{c.name}</h3>
          <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600">
            <span className="flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" />{fmtDate(c.start_date)} – {fmtDate(c.end_date)}</span>
            <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{c.start_time ? `${fmtTime(c.start_time)} – ${fmtTime(c.end_time)}` : c.schedule_note || "Schedule confirmed at enrollment"}</span>
            <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{c.format}</span>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-3 sm:flex-col sm:items-end">
        <SeatBadge c={c} />
        <Link to={`/apply?program=${c.program_id}&class=${c.id}`} onClick={() => track("registration_attempt", { program_id: c.program_id })} className={open ? "btn-primary btn-sm" : "btn-outline btn-sm"} data-testid={`class-register-btn-${c.id}`}>
          {open ? "Reserve a Seat" : "Join Waitlist"}
        </Link>
      </div>
    </div>
  );
}

export function FaqList({ faqs, testid = "faq-list" }) {
  return (
    <Accordion type="single" collapsible className="card divide-y divide-slate-100 px-5" data-testid={testid}>
      {faqs.map((f, i) => (
        <AccordionItem key={f.id} value={f.id} className="border-0">
          <AccordionTrigger className="text-left font-heading font-semibold text-slate-900 hover:no-underline" data-testid={`faq-item-${i}`}>{f.question}</AccordionTrigger>
          <AccordionContent className="text-slate-600">{f.answer}</AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
}

export function StaffCard({ s }) {
  return (
    <div className="card p-6 text-center" data-testid={`staff-card-${s.id}`}>
      {s.photo_url ? <img src={s.photo_url} alt={s.name} loading="lazy" className="mx-auto h-24 w-24 rounded-full object-cover" />
        : <div className="mx-auto grid h-24 w-24 place-items-center rounded-full bg-slate-900 font-heading text-2xl font-bold text-white">{s.name.split(" ").map((x) => x[0]).join("")}</div>}
      <h3 className="mt-4 font-heading font-bold text-slate-900">{s.name}</h3>
      <p className="text-sm font-semibold text-red-600">{s.role_title}</p>
      {s.credentials && <p className="mt-1 text-xs text-slate-500">{s.credentials}</p>}
      {s.bio && <p className="mt-3 text-sm text-slate-600">{s.bio}</p>}
    </div>
  );
}

export function TestimonialCard({ t }) {
  return (
    <figure className="card p-6" data-testid={`testimonial-${t.id}`}>
      <Quote className="h-6 w-6 text-red-500" />
      <blockquote className="mt-3 text-slate-700">{t.text}</blockquote>
      <figcaption className="mt-4 text-sm font-bold text-slate-900">{t.student_name}<span className="block text-xs font-medium text-slate-500">{t.program_name}</span></figcaption>
    </figure>
  );
}

export function CtaBlock({ title = <>Ready to start your <span className="text-red-500">healthcare career</span>?</>, text = "Talk with Admissions, compare programs, or start your application today." }) {
  return (
    <section className="container-x py-16">
      <div className="relative overflow-hidden rounded-3xl bg-slate-900 px-6 py-12 text-white sm:px-12">
        <svg className="pointer-events-none absolute -right-10 bottom-0 h-40 w-[520px] opacity-30" viewBox="0 0 520 120" aria-hidden><polyline className="ekg-line" fill="none" stroke="#ef4444" strokeWidth="4" points="0,80 140,80 170,40 200,80 250,80 280,10 310,110 340,80 520,80" /></svg>
        <div className="relative max-w-2xl">
          <h2 className="font-heading text-3xl font-extrabold sm:text-4xl">{title}</h2>
          <p className="mt-3 text-slate-300">{text}</p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Link to="/apply" className="btn-primary" data-testid="cta-block-apply">Start Your Application <ArrowRight className="h-4 w-4" /></Link>
            <Link to="/find-your-program" className="btn-outline border-slate-700 bg-transparent text-white hover:border-white hover:text-white" data-testid="cta-block-finder">Find the Right Program</Link>
          </div>
        </div>
      </div>
    </section>
  );
}

export const PageHero = ({ eyebrow, title, children }) => (
  <section className="border-b border-slate-100 bg-white">
    <div className="container-x py-14 sm:py-20">
      {eyebrow && <p className="eyebrow">{eyebrow}</p>}
      <h1 className="mt-3 max-w-4xl font-heading text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">{title}</h1>
      {children && <div className="mt-5 max-w-2xl text-base text-slate-600 md:text-lg">{children}</div>}
    </div>
  </section>
);

export const Benefit = ({ icon: Icon = Award, title, children }) => (
  <div className="flex gap-4">
    <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-red-50 text-red-600"><Icon className="h-5 w-5" /></span>
    <div><h3 className="font-heading font-bold text-slate-900">{title}</h3><p className="mt-1 text-sm text-slate-600">{children}</p></div>
  </div>
);

export function RichText({ text = "" }) {
  return (
    <div className="prose-hr">
      {text.split("\n\n").map((para, i) => (
        <p key={i}>{para.split(/(\*\*[^*]+\*\*)/).map((s, j) => (s.startsWith("**") ? <strong key={j}>{s.slice(2, -2)}</strong> : s))}</p>
      ))}
    </div>
  );
}
